import { makeAutoObservable, runInAction } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import {
  type Conversation,
  type ConversationsStore,
  type IConversationSignal,
  type IConversationSignalOpenNewConversation,
} from 'entities/Conversation'
import { type ConversationGroupError } from 'entities/Conversation/model/ConversationGroupError'
import {
  type IConversationSignalOpenExistConversation,
  type IConversationSignalOpenGroupConversation,
} from 'entities/Conversation/store/types'

export class ConversationOpenStore {
  private _signal: IConversationSignal | null = null
  private _loading = false

  constructor(private _conversationsStore: ConversationsStore) {
    makeAutoObservable(this)
  }

  setSignal = async (signal: IConversationSignal) => {
    this._signal = signal

    if (signal.type === 'open_new_conversation') {
      this.handleOpenNewConversation(signal.payload)

      return
    }

    if (signal.type === 'open_exist_conversation') {
      await this.handleOpenExistingConversation(signal.payload)

      return
    }

    if (signal.type === 'open_group_conversation') {
      this.handleOpenGroupConversation(signal.payload)
    }
  }

  handleOpenNewConversation = (payload?: IConversationSignalOpenNewConversation['payload']) => {
    this._conversationsStore.createNewConversation(payload?.contact)
    this._conversationsStore.setCurrentItemId(0)
    uiStore.changeRoute({
      path: '/conversations/0',
      options: {
        state: {
          inboxId: payload?.inbox?.id,
          numberId: payload?.numberId,
        },
      },
    })
  }

  handleCloseNewConversation = () => {
    this._conversationsStore.setCurrentItemId(null)
    this._conversationsStore.deleteItem(0)
    uiStore.changeRoute({
      path: '/conversations',
    })
  }

  handleOpenExistingConversation = async (
    payload: IConversationSignalOpenExistConversation['payload']
  ) => {
    const inboxId = payload.inbox_id
    const existingConversation = this._conversationsStore.getItem(payload.id)
    const promises: Promise<void | Conversation | ConversationGroupError | undefined>[] = []

    if (this._conversationsStore.inboxesStore) {
      promises.push(this._conversationsStore.inboxesStore.handleUpdateTeamInbox(inboxId))
    }

    if (!existingConversation || !existingConversation.isClosed) {
      promises.push(
        this._conversationsStore.createConversation({
          contact_id: payload.contact_id,
          team_id: inboxId,
        })
      )
    }

    runInAction(() => {
      this._loading = true
    })

    await Promise.all(promises)

    runInAction(() => {
      this._loading = false
    })

    this._conversationsStore.setCurrentItemId(payload.id)
    uiStore.changeRoute({
      path: `/conversations/${payload.id}`,
      query: 'decline_onload=',
      options: {
        state: {
          from: 'openExistingConversation',
        },
      },
    })
  }

  handleOpenGroupConversation = (payload: IConversationSignalOpenGroupConversation['payload']) => {
    this._conversationsStore.reset()
    this._conversationsStore.resetCurrentItem()
    uiStore.changeRoute({
      path: `/conversations/${payload.conversation.id}`,
    })
  }

  get signal() {
    return this._signal
  }

  get loading() {
    return this._loading
  }

  clearSignal = () => {
    this._signal = null
  }
}
